import { type ReactNode } from 'react';
import * as Sentry from '@sentry/react';

import { Blockquote, Button, Center, Code, Group, rem, Stack, Text, Title } from '@mantine/core';
import { IconBrandSentry } from '@tabler/icons-react';

import { CanaryAnimation } from './Canary';

interface Props {
  children: ReactNode;
}

function Fallback({
  error,
  componentStack,
  resetError,
}: {
  error: unknown;
  componentStack: string;
  resetError: () => void;
}) {
  const environment = import.meta.env.VITE_SENTRY_ENVIRONMENT || 'unknown';

  const isError = error instanceof Error;

  return (
    <Center mih="100vh" p="md">
      <Stack align="center" gap="md" maw={rem(480)} ta="center">
        {/* <Image src="/images/anim/canary.svg" alt="Canary's Down" /> */}
        <CanaryAnimation />

        <Title order={2} c="ore.4">
          The Canary's Down
        </Title>
        {environment === 'dev' ? (
          <Blockquote
            color="red.6"
            radius="xs"
            iconSize={30}
            cite={isError ? error.message : String(error)}
            icon={<IconBrandSentry size={30} />}
            mt="xs"
          >
            <Stack>
              <Title order={3} c="red.6">
                {isError ? `${error.name}: ${error.message}` : String(error)}
              </Title>
              <Code block bg="dark" c="ore.1">
                {isError ? error.stack : String(error)}
              </Code>
              {componentStack.split('\n').slice(0, 5).join('\n')}
            </Stack>
          </Blockquote>
        ) : (
          <Text c="dimmed">
            Something went wrong. We've been notified and we're on it. Hit{' '}
            <strong>Try again</strong> to pick up where you left off, or reload if things still
            smell bad.
          </Text>
        )}
        <Group>
          <Button variant="default" onClick={resetError}>
            Try again
          </Button>
          <Button onClick={() => globalThis.location.reload()}>Reload app</Button>
        </Group>
      </Stack>
    </Center>
  );
}

export function AppErrorBoundary({ children }: Props) {
  return (
    <Sentry.ErrorBoundary fallback={Fallback} showDialog>
      {children}
    </Sentry.ErrorBoundary>
  );
}
